// src/store/learnerAttendanceSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // central client with REACT_APP_BASE_URL and API_KEY

// ——— Thunks —————————————————————————————————————————————————————————————

// Fetch all attendance for one training-card
export const fetchAttendanceByTrainingCard = createAsyncThunk(
  "learnerAttendance/fetchByTrainingCard",
  async (tcId, { rejectWithValue }) => {
    if (!tcId) return rejectWithValue("tcId required");
    try {
      const { data } = await apiClient.get(`/training-cards/${tcId}/learner-attendance`);
      return { tcId, data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Insert a new attendance record
export const insertLearnerAttendance = createAsyncThunk(
  "learnerAttendance/insert",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(`/learner-attendance/`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Patch only the P/A field
export const patchAttendanceOnly = createAsyncThunk(
  "learnerAttendance/patchOnly",
  async ({ id, Attendance }, { rejectWithValue }) => {
    if (!id) return rejectWithValue("missing id");
    try {
      const res = await apiClient.patch(`/attendance/${id}/attendance`, { Attendance });
      return res.data && Object.keys(res.data).length
        ? res.data
        : { id, Attendance };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ——— Slice —————————————————————————————————————————————————————————————
const initialState = {
  byTrainingCard: {}, // { [tcId]: [attendanceRecord, …] }
  loading: false,     // for fetch
  saving: false,      // for patch
  inserting: false,   // for insert
  error: null,
};

const learnerAttendanceSlice = createSlice({
  name: "learnerAttendance",
  initialState,
  reducers: {},
  extraReducers: (b) => {
    // — FETCH BY TC —
    b.addCase(fetchAttendanceByTrainingCard.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchAttendanceByTrainingCard.fulfilled, (s, a) => {
      s.loading = false;
      s.byTrainingCard[a.payload.tcId] = a.payload.data;
    });
    b.addCase(fetchAttendanceByTrainingCard.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload;
    });

    // — INSERT —
    b.addCase(insertLearnerAttendance.pending, (s) => {
      s.inserting = true;
      s.error = null;
    });
    b.addCase(insertLearnerAttendance.fulfilled, (s, a) => {
      s.inserting = false;
      const rec = a.payload;
      if (rec.tc_id in s.byTrainingCard) {
        s.byTrainingCard[rec.tc_id].push(rec);
      } else {
        s.byTrainingCard[rec.tc_id] = [rec];
      }
    });
    b.addCase(insertLearnerAttendance.rejected, (s, a) => {
      s.inserting = false;
      s.error = a.payload;
    });

    // — PATCH ONLY Attendance —
    b.addCase(patchAttendanceOnly.pending, (s) => {
      s.saving = true;
      s.error = null;
    });
    b.addCase(patchAttendanceOnly.fulfilled, (s, a) => {
      s.saving = false;
      const updated = a.payload;
      Object.values(s.byTrainingCard).forEach((arr) => {
        const idx = arr.findIndex((r) => r.id === updated.id);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], ...updated };
        }
      });
    });
    b.addCase(patchAttendanceOnly.rejected, (s, a) => {
      s.saving = false;
      s.error = a.payload;
    });
  },
});

export default learnerAttendanceSlice.reducer;
